import React from 'react';
import { Routes, Route } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { MsalProvider, AuthenticatedTemplate, UnauthenticatedTemplate } from '@azure/msal-react';

// Layout components
import Layout from './components/Layout/Layout';

// Pages
import HomePage from './pages/HomePage';
import DocumentPage from './pages/DocumentPage';
import SearchPage from './pages/SearchPage';
import EditDocumentPage from './pages/EditDocumentPage';
import NotFoundPage from './pages/NotFoundPage';
import LoginPage from './pages/LoginPage';
// Theme configuration
const theme = createTheme({
  palette: {
    primary: {
      light: '#8e3a6d', // lighter shade of primary
      main: '#6c0c46', // brand color
      dark: '#4a0030', // darker shade for hover states
      contrastText: '#ffffff',
    },
    secondary: {
      light: '#6ca0b6',
      main: '#3d7185', // complementary color
      dark: '#1d4457',
      contrastText: '#ffffff',
    },
    background: {
      default: '#fafafa',
      paper: '#ffffff',
    },
    error: {
      main: '#d32f2f',
    },
    warning: {
      main: '#ed6c02',
    },
    info: {
      main: '#0288d1',
    },
    success: {
      main: '#2e7d32',
    },
    text: {
      primary: '#333333',
      secondary: '#666666',
    },
  },
  typography: {
    fontFamily: [
      'Roboto',
      '"Segoe UI"',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif',
    ].join(','),
    h1: {
      fontWeight: 500,
      fontSize: '2.5rem',
    },
    h2: {
      fontWeight: 500,
      fontSize: '2rem',
    },
    h3: {
      fontWeight: 500,
      fontSize: '1.8rem',
    },
    h4: {
      fontWeight: 500,
      fontSize: '1.6rem',
    },
    h5: {
      fontWeight: 500,
      fontSize: '1.4rem',
    },
    h6: {
      fontWeight: 500,
      fontSize: '1.2rem',
    },
    subtitle1: {
      fontSize: '1.1rem',
      fontWeight: 500,
    },
    subtitle2: {
      fontSize: '0.9rem',
      fontWeight: 500,
    },
  },
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 500,
        },
        containedPrimary: {
          '&:hover': {
            backgroundColor: '#4a0030', // darker shade for hover
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          transition: 'transform 0.3s, box-shadow 0.3s',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: '0 6px 12px rgba(0,0,0,0.1)',
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            '&:hover fieldset': {
              borderColor: '#8e3a6d',
            },
          },
        },
      },
    },
  },
});

/**
 * Main App component
 */
const App = ({ msalInstance }) => {
  // Check if we're in local development and don't have MSAL configured
  const isLocalDevelopment = process.env.NODE_ENV === 'development' && 
    (!process.env.REACT_APP_MSAL_CLIENT_ID || !process.env.REACT_APP_MSAL_AUTHORITY);

  return (
    <MsalProvider instance={msalInstance}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {isLocalDevelopment ? (
          // Local development mode - bypass authentication
          <Layout>
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/search" element={<SearchPage />} />
              <Route path="/documents/:id" element={<DocumentPage />} />
              <Route path="/documents/:id/edit" element={<EditDocumentPage />} />
              <Route path="/documents/new" element={<EditDocumentPage />} />
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </Layout>
        ) : (
          // Production mode - require authentication
          <>
            <UnauthenticatedTemplate>
              <Routes>
                <Route path="/" element={<LoginPage />} />
                <Route path="*" element={<LoginPage />} />
              </Routes>
            </UnauthenticatedTemplate>
            <AuthenticatedTemplate>
              <Layout>
                <Routes>
                  <Route path="/" element={<HomePage />} />
                  <Route path="/search" element={<SearchPage />} />
                  <Route path="/documents/:id" element={<DocumentPage />} />
                  <Route path="/documents/:id/edit" element={<EditDocumentPage />} />
                  <Route path="/documents/new" element={<EditDocumentPage />} />
                  <Route path="*" element={<NotFoundPage />} />
                </Routes>
              </Layout>
            </AuthenticatedTemplate>
          </>
        )}
      </ThemeProvider>
    </MsalProvider>
  );
}

export default App;
